import React, {useState, useEffect, useRef} from 'react'
import {Box, Text, HStack, StackProps, useMultiStyleConfig, Link, TextProps, VStack} from '@chakra-ui/react'
import {useViewport, useHeaderSizeWithoutBanner, useAnalytics} from '_customHooks'
import {getLabel, getHierarchy, getAnalyticsSecondLevel} from '_analytics/utils/analyticsUtils'
import {FormattedMessage} from 'react-intl'
import {CategoryMenu, SubCategoryMenu} from '_types'
import {Picture, MenuItem, MenuButton} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHeaderState} from '_context'

interface CatalogMenuProps {
    category: CategoryMenu
    menuStaticLibrary: string
    closeMenu: () => void
    tabIndex?: number
}

const CatalogMenu = ({category, menuStaticLibrary, closeMenu, tabIndex}: CatalogMenuProps): JSX.Element => {
    const {isDesktop, isMobile} = useViewport()
    const {locale} = useMultiSite()
    const {isAtTop} = useHeaderState()
    const {analyticsEventMenuClick} = useAnalytics()
    const headerSize = useHeaderSizeWithoutBanner(!isAtTop)
    const editorialLength = category.editorial?.length || 0
    const styles = useMultiStyleConfig('SecondLevelMenu', {editorialLength, headerSize}) as Record<string, StackProps & TextProps>

    // Checks if a category or subcategory should be visible for the current viewport
    const shouldBeVisible = (category) => {
        return !(isMobile && category.hideForMobile) && !(isDesktop && category.hideForDesktop)
    }
    // Build grouped subcategories by forceLastInColumn, only visible categories
    let groupedSubcategories: SubCategoryMenu[][] = []
    if (category.subcategories && category.subcategories.length > 0) {
        let currentGroup: SubCategoryMenu[] = []
        for (const subcat of category.subcategories) {
            if (shouldBeVisible(subcat)) {
                currentGroup.push(subcat)
            }
            if (subcat.forceLastInColumn) {
                if (currentGroup.length > 0) {
                    groupedSubcategories.push(currentGroup)
                }
                currentGroup = []
            }
        }
        if (currentGroup.length > 0) {
            groupedSubcategories.push(currentGroup)
        }
    }
    // On mobile & tablet, move only the category with isSale === true to a new group at the first position
    if (!isDesktop && groupedSubcategories.length > 0) {
        let saleCat: SubCategoryMenu | undefined = undefined
        let saleGroupIdx = -1
        let saleCatIdx = -1
        for (let i = 0; i < groupedSubcategories.length; i++) {
            const group = groupedSubcategories[i]
            const idx = group.findIndex((cat) => cat.isSale === true)
            if (idx !== -1) {
                saleCat = group[idx]
                saleGroupIdx = i
                saleCatIdx = idx
                break
            }
        }
        if (saleCat && saleGroupIdx !== -1 && saleCatIdx !== -1) {
            // Remove saleCat from its group
            groupedSubcategories[saleGroupIdx].splice(saleCatIdx, 1)
            // Remove empty group if needed
            if (groupedSubcategories[saleGroupIdx].length === 0) {
                groupedSubcategories.splice(saleGroupIdx, 1)
            }
            // Add saleCat as its own group at the start
            groupedSubcategories = [[saleCat], ...groupedSubcategories]
        }
    }
    const isTouchEnabled = useRef(false)
    useEffect(() => {
        if (window && window.matchMedia('(pointer: coarse)').matches) {
            // touchscreen
            isTouchEnabled.current = true
        }
    }, [])
    // image trasformation for device it depend on the number of editorial items
    const imageTransformation = {
        default: ['300', '400', '600'],
        2: ['500', '600', '650'],
        3: ['300', '400', '600'],
    }
    const editorialItems = category.editorial
    const scaleImage = imageTransformation[editorialLength] || imageTransformation.default
    const analyticsTemplate = category.analyticsTemplate || ''
    const mainMenu = category.analytics
    const firstLevelCategoryName = category.name.toUpperCase()
    const variantToShow = !isDesktop ? 'header' : 'title'

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

    const handleCategoryClick = (categoryId: string) => {
        if (isDesktop && isTouchEnabled.current) {
            closeMenu()
        } else if (!isDesktop && categoryId) {
            setSelectedCategory(categoryId === selectedCategory ? null : categoryId)
        }
    }

    const handleBackClick = () => {
        setSelectedCategory(null)
    }

    return (
        <>
            <HStack data-testid="catalog-menu" {...styles.catalog} aria-hidden={true}>
                <Box {...styles.catalogContainer}>
                    {groupedSubcategories.map((group, groupIdx) => (
                        <VStack key={`group-${groupIdx}`} {...styles.catalogBox}>
                            {group
                                .filter((groupCategory) => groupCategory && Object.keys(groupCategory).length > 0)
                                .map((groupCategory, categoryIdx) => (
                                    <React.Fragment key={`group-${groupIdx}-category-${categoryIdx}`}>
                                        <Box
                                            {...styles.subcategoryBox}
                                            {...(variantToShow === 'title' ? styles.linkTextTitle : undefined)}
                                            onClick={() => handleCategoryClick(`group-${groupIdx}-category-${categoryIdx}`)}
                                        >
                                            <MenuItem
                                                href={groupCategory.url || '#'}
                                                variant={variantToShow}
                                                analyticsEventData={getAnalyticsSecondLevel({
                                                    category: groupCategory,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'main',
                                                })}
                                                renderaslink={isDesktop || (isMobile && !groupCategory?.subcategories)}
                                                arrow={isDesktop ? 'none' : 'right'}
                                                tabIndex={tabIndex}
                                            >
                                                <Box as="span" {...styles.subcategory}>
                                                    {groupCategory.name.toUpperCase()}
                                                </Box>
                                            </MenuItem>
                                        </Box>
                                        {groupCategory.subcategories && groupCategory.subcategories.length > 0 && (
                                            <Box
                                                {...styles.subcategoryBisBox}
                                                {...(selectedCategory === `group-${groupIdx}-category-${categoryIdx}`
                                                    ? styles.subcategoryBisBoxOpen
                                                    : undefined)}
                                                data-testid="subcategory-bis-box"
                                            >
                                                {!isDesktop && (
                                                    <Box {...styles.secondLevelBack} onClick={() => handleBackClick()}>
                                                        <MenuItem href={groupCategory.url || '#'} variant={variantToShow} renderaslink={isDesktop} arrow="left">
                                                            {/* `as="span"`: MenuItem wraps children in a Chakra Text (<p>), so a plain
                                                                Box (<div>) here triggers "div cannot appear as a descendant of p". */}
                                                            <Box as="span" {...styles.subcategory}>
                                                                {firstLevelCategoryName + ' / ' + groupCategory.name.toUpperCase()}
                                                            </Box>
                                                        </MenuItem>
                                                    </Box>
                                                )}
                                                <Box {...styles.subcategoryBisBoxContent}>
                                                    {groupCategory.subcategories?.map((subcategory, subcategoryIdx) => (
                                                        <Box {...styles.subcategoryBis} key={`group-${groupIdx}-category-${categoryIdx}-sub${subcategoryIdx}`}>
                                                            <MenuItem
                                                                href={subcategory.url || '#'}
                                                                variant="navigation"
                                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                                analyticsEventData={getAnalyticsSecondLevel({
                                                                    category: groupCategory,
                                                                    subcategory: subcategory,
                                                                    mainMenu: mainMenu,
                                                                    analyticsTemplate: analyticsTemplate,
                                                                    position: 'main',
                                                                })}
                                                                arrow={isDesktop ? 'none' : 'right'}
                                                                tabIndex={tabIndex}
                                                            >
                                                                {subcategory.name}
                                                            </MenuItem>
                                                        </Box>
                                                    ))}
                                                    {!isDesktop && groupCategory.show_all && (
                                                        <Box {...styles.catalogShowAll}>
                                                            {groupCategory.url && groupCategory.show_all && (
                                                                <MenuButton
                                                                    href={groupCategory.url || '#'}
                                                                    variant={'footer'}
                                                                    closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                                    analyticsEventData={getAnalyticsSecondLevel({
                                                                        category: groupCategory,
                                                                        subcategory: null,
                                                                        mainMenu: mainMenu,
                                                                        analyticsTemplate: analyticsTemplate,
                                                                        position: 'footer',
                                                                    })}
                                                                >
                                                                    {groupCategory.show_all.toUpperCase()}
                                                                </MenuButton>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                    </React.Fragment>
                                ))}
                        </VStack>
                    ))}
                </Box>

                {editorialItems && (
                    <Box {...styles.editorialContainer}>
                        <Box {...styles.catalogEditorialBox}>
                            {editorialItems.map((editorialItem) => (
                                <Box {...styles.editorialWidth} key={editorialItem.cgid ? editorialItem.cgid : editorialItem.url}>
                                    <Box {...styles.editorialTitle} {...(variantToShow === 'title' ? styles.linkTextTitle : undefined)}>
                                        <MenuItem
                                            href={editorialItem.url || '#'}
                                            variant={variantToShow}
                                            closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                            analyticsEventData={getAnalyticsSecondLevel({
                                                category: editorialItem,
                                                subcategory: null,
                                                mainMenu: mainMenu,
                                                analyticsTemplate: analyticsTemplate,
                                                position: 'big image box',
                                            })}
                                            tabIndex={tabIndex}
                                            hasPadding={false}
                                            hasInlineBlock={true}
                                        >
                                            {editorialItem.name.toUpperCase()}
                                        </MenuItem>
                                        {!isDesktop && (
                                            <MenuButton
                                                href={editorialItem.url || '#'}
                                                variant={'underline'}
                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                analyticsEventData={getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })}
                                            >
                                                <FormattedMessage id="label.discover" defaultMessage="Discover More" />
                                            </MenuButton>
                                        )}
                                    </Box>
                                    {!isDesktop && (
                                        <Box
                                            {...styles.editorialPitcure}
                                            as={(editorialItem?.url ?? '').indexOf('https://') > -1 ? 'a' : Link}
                                            href={
                                                (editorialItem?.url ?? '').indexOf('https://') > -1
                                                    ? editorialItem?.url
                                                    : `/${locale.alias}${editorialItem?.url}`
                                            }
                                            onClick={() => {
                                                const analyticsEventData = getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })
                                                analyticsEventMenuClick({
                                                    action: analyticsEventData?.mainMenu || '',
                                                    label: getLabel(analyticsEventData),
                                                    hierarchy: getHierarchy(analyticsEventData),
                                                    menuTemplate: analyticsEventData?.analyticsTemplate || '',
                                                })
                                            }}
                                        >
                                            <Picture
                                                objectFit="cover"
                                                zIndex={1}
                                                position="absolute"
                                                imagexs={menuStaticLibrary + editorialItem.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                imagelg={menuStaticLibrary + editorialItem.image_tab + '?sw=' + scaleImage[1] + '&sfrm=jpg'}
                                                imagexl={menuStaticLibrary + editorialItem.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                alt={`${category.name} - ${editorialItem.name}`}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                        {isDesktop && (
                            <Box {...styles.catalogEditorialBox}>
                                {editorialItems.map((editorialItem, index) => (
                                    <Box {...styles.editorialWidth} key={`pitc-${editorialItem.cgid ? editorialItem.cgid : editorialItem.url}-${index}`}>
                                        <Box
                                            {...styles.editorialPitcure}
                                            as={(editorialItem?.url ?? '').indexOf('https://') > -1 ? 'a' : Link}
                                            href={
                                                (editorialItem?.url ?? '').indexOf('https://') > -1
                                                    ? editorialItem?.url
                                                    : `/${locale.alias}${editorialItem?.url}`
                                            }
                                            onClick={() => {
                                                const analyticsEventData = getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })
                                                analyticsEventMenuClick({
                                                    action: analyticsEventData?.mainMenu || '',
                                                    label: getLabel(analyticsEventData),
                                                    hierarchy: getHierarchy(analyticsEventData),
                                                    menuTemplate: analyticsEventData?.analyticsTemplate || '',
                                                })
                                            }}
                                        >
                                            <Picture
                                                objectFit="cover"
                                                zIndex={1}
                                                position="absolute"
                                                imagexs={menuStaticLibrary + editorialItem.image_mob + '?sw=' + scaleImage[2] + '&sfrm=jpg'}
                                                imagelg={menuStaticLibrary + editorialItem.image_tab + '?sw=' + scaleImage[1] + '&sfrm=jpg'}
                                                imagexl={menuStaticLibrary + editorialItem.image + '?sw=' + scaleImage[0] + '&sfrm=jpg'}
                                                alt={`${category.name} - ${editorialItem.name}`}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {isDesktop && (
                            <Box {...styles.catalogEditorialBox}>
                                {editorialItems.map((editorialItem, index) => (
                                    <Box {...styles.editorialWidth} key={`link-${editorialItem.cgid ? editorialItem.cgid : editorialItem.url}-${index}`}>
                                        <Box {...styles.hiddenforMobile}>
                                            <MenuItem
                                                href={editorialItem.url || '#'}
                                                variant={'navigation'}
                                                closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                                                analyticsEventData={getAnalyticsSecondLevel({
                                                    category: editorialItem,
                                                    subcategory: null,
                                                    mainMenu: mainMenu,
                                                    analyticsTemplate: analyticsTemplate,
                                                    position: 'big image box',
                                                })}
                                                tabIndex={tabIndex}
                                            >
                                                <Text as="span" {...styles.discoverMoreDesktop}>
                                                    <FormattedMessage id="label.discover" defaultMessage="Discover More" />
                                                </Text>
                                            </MenuItem>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </HStack>
            <Box {...styles.catalogShowAll}>
                {category.url && category.show_all && (
                    <MenuButton
                        href={category.url || '#'}
                        variant={'footer'}
                        closeMenu={isDesktop && isTouchEnabled.current ? closeMenu : undefined}
                        analyticsEventData={getAnalyticsSecondLevel({
                            category: category,
                            subcategory: null,
                            mainMenu: mainMenu,
                            analyticsTemplate: analyticsTemplate,
                            position: 'footer',
                        })}
                    >
                        {category.show_all.toUpperCase()}
                    </MenuButton>
                )}
            </Box>
        </>
    )
}

export default CatalogMenu
